"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calculator, Compass, Hammer, Bath, House as Home } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface GuideCard {
  id: string;
  title: string;
  description: string;
  to: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const guides: GuideCard[] = [{
  id: "cost-guide",
  title: "Remodeling Cost Guide",
  description: "Explore project budgeting tiers, material options, and local Decatur investment benchmarks.",
  to: "/DecaturRemodelingCostGuidePage",
  icon: Calculator
}, {
  id: "planning-guide",
  title: "Remodeling Planning Guide",
  description: "Review architectural timelines, local permitting considerations, and pre-construction preparation.",
  to: "/DecaturRemodelingPlanningGuidePage",
  icon: Compass
}, {
  id: "process",
  title: "Process & Planning",
  description: "Follow our step-by-step design-build approach from initial concept through punch list completion.",
  to: "/ProcessPlanningPage",
  icon: Hammer
}, {
  id: "bathrooms",
  title: "Bathroom Remodeling",
  description: "Discover custom master ensuites, walk-in tile showers, and timeless vanity transformations.",
  to: "/BathroomRemodelingPage",
  icon: Bath
}, {
  id: "whole-home",
  title: "Whole-Home Remodeling",
  description: "Learn about full-scale residential renovations, historic preservation, and floor plan reconfigurations.",
  to: "/WholeHomeRoomRemodelingPage",
  icon: Home
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export default function RelatedGuides() {
  return <section id="relatedguidesandservices" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {/* Decorative ambient background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 size-96 rounded-full bg-secondary/70 blur-3xl opacity-60" />
        <div className="absolute -bottom-24 -right-24 size-96 rounded-full bg-secondary/50 blur-3xl opacity-50" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12 lg:mb-16">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            Explore Related Guides &amp; Services
          </h2>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {guides.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={i} variants={cardVariants} whileHover={{
            y: -4
          }} transition={{
            duration: 0.3
          }} className="group relative flex flex-col justify-between rounded-xl border border-border/80 bg-card/80 backdrop-blur-md p-6 sm:p-8 shadow-sm hover:shadow-md hover:border-chart-1/30 transition-all duration-300">
                <div>
                  <div className="size-12 rounded-lg bg-secondary text-foreground flex items-center justify-center mb-5 group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <IconComponent className="size-5 text-chart-1 group-hover:text-primary-foreground transition-colors duration-300" />
                  </div>

                  <h3 className="font-serif text-xl sm:text-2xl font-medium text-card-foreground mb-3 tracking-tight">
                    {item.title}
                  </h3>

                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-6">
                    {item.description}
                  </p>
                </div>

                <div className="pt-2 border-t border-border/50">
                  <Button variant="link" asChild className="p-0 h-auto font-medium text-foreground group-hover:text-chart-1 inline-flex items-center gap-2 transition-colors duration-200">
                    <Link to={item.to} className="inline-flex items-center gap-1.5">
                      <span>View details</span>
                      <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1 text-chart-1" />
                    </Link>
                  </Button>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}